"use client"

import {
  memo,
  useEffect,
  useRef,
} from "react"

import { Drawer } from "vaul"
import {
  motion,
  PanInfo,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion"

import useOrientation from "@/hooks/useOrientation"
import DrawerBackground from "./DrawerBackground"

type Props = {
  open: boolean
  setOpen: (v: boolean) => void
  children: React.ReactNode
  image?: string
  snap?: number
  setSnap?: (v: string | number | null) => void
  originY?: number
}

const CLOSE_DISTANCE_RATIO = 0.42
const CLOSE_VELOCITY = 650

function DrawerContainer({
  open,
  setOpen,
  children,
  image,
}: Props) {
  const {
    isPortrait,
    isLandscape,
    isStandalone,
  } = useOrientation()

  const contentRef = useRef<HTMLDivElement>(null)

  const draggingRef = useRef(false)
  const startScrollRef = useRef(0)

  /*
   * MotionValue:
   *
   * El drawer sigue directamente al dedo.
   * No pasa por setState -> render -> animation.
   */
  const y = useMotionValue(0)

  /*
   * Pequeña resistencia cuando se intenta
   * arrastrar hacia arriba.
   */
  const shadowOpacity = useTransform(
    y,
    [0, 200, 500],
    [0.65, 0.45, 0.2]
  )

  /*
   * BODY LOCK
   */
  useEffect(() => {
    if (!open) return

    const previous =
      document.body.style.overflow

    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  /*
   * RESET
   */
  useEffect(() => {
    if (open) {
      y.set(0)
    }
  }, [open, y])

  /*
   * ¿Puede el drawer capturar el gesto?
   *
   * Solo cuando el contenido está arriba.
   */
  const canStartDrawerDrag = (
    event: PointerEvent
  ) => {
    const content =
      contentRef.current

    if (!content) {
      return false
    }

    if (
      event.pointerType === "mouse"
    ) {
      return false
    }

    /*
     * Si hay scroll arriba del todo,
     * el gesto pertenece al contenido.
     */
    if (content.scrollTop > 0) {
      return false
    }

    return true
  }

  /*
   * POINTER DOWN
   */
  const handlePointerDown = (
    event: React.PointerEvent
  ) => {
    const canDrag =
      canStartDrawerDrag(
        event.nativeEvent
      )

    draggingRef.current =
      canDrag

    startScrollRef.current =
      contentRef.current?.scrollTop ?? 0
  }

  /*
   * DRAG START
   */
  const handleDragStart = () => {
    if (!draggingRef.current) {
      return
    }

    const content =
      contentRef.current

    if (!content) {
      draggingRef.current = false
      return
    }

    /*
     * Protección contra empezar el drag
     * mientras el contenido está desplazado.
     */
    if (content.scrollTop > 0) {
      draggingRef.current = false
      return
    }
  }

  /*
   * DRAG
   */
  const handleDrag = (
    _event:
      | MouseEvent
      | TouchEvent
      | PointerEvent,
    info: PanInfo
  ) => {
    if (!draggingRef.current) {
      return
    }

    /*
     * Seguimiento 1:1.
     *
     * No usamos setState.
     * No usamos animate durante el gesto.
     */
    const offset = info.offset.y

    /*
     * Nunca permitimos subir el drawer
     * por encima de su posición original.
     */
    if (offset <= 0) {
      y.set(0)
      return
    }

    y.set(offset)
  }

  /*
   * DRAG END
   */
  const handleDragEnd = (
    _event:
      | MouseEvent
      | TouchEvent
      | PointerEvent,
    info: PanInfo
  ) => {
    if (!draggingRef.current) {
      return
    }

    draggingRef.current = false

    const distance =
      Math.max(0, info.offset.y)

    const velocity =
      Math.max(0, info.velocity.y)

    /*
     * Altura real de la ventana.
     *
     * Ejemplo:
     *
     * 800px -> cerrar a partir de 336px
     * 900px -> cerrar a partir de 378px
     */
    const closeDistance =
      window.innerHeight *
      CLOSE_DISTANCE_RATIO

    const shouldClose =
      distance >= closeDistance ||
      velocity >= CLOSE_VELOCITY

    if (shouldClose) {
      /*
       * Primero terminamos físicamente
       * el movimiento del drawer.
       *
       * Después cerramos Vaul.
       */
      const target =
        window.innerHeight + 40

      animate(y, target, {
        type: "spring",
        stiffness: 430,
        damping: 42,
        mass: 0.75,
        velocity: info.velocity.y,
        onComplete: () => {
          setOpen(false)
          y.set(0)
        },
      })

      return
    }

    /*
     * No llegó al umbral:
     * vuelve suavemente a 0.
     */
    animate(y, 0, {
      type: "spring",
      stiffness: 430,
      damping: 36,
      mass: 0.75,
      velocity: info.velocity.y,
    })
  }

  return (
    <Drawer.Root
      open={open}
      onOpenChange={setOpen}
      modal
      dismissible
      shouldScaleBackground
      fadeFromIndex={0}
      snapPoints={[1]}
      activeSnapPoint={1}
    >
      <Drawer.Portal>

        {/* BACKDROP */}

        <Drawer.Overlay
          className="
            fixed
            h-[100dvh]
            inset-0
            z-[120]
            bg-black/65
            backdrop-blur-xl
            //bg-white/90
          "
          style={{
    overscrollBehavior: "none",
    contain: "layout paint size",
  }}
        />

        {/* DRAWER */}

        <Drawer.Content
  asChild
  forceMount
  style={{
    overscrollBehavior: "none",
    contain: "layout paint size",
    WebkitOverflowScrolling: "auto",
    touchAction: "none",
  }}
>


          <motion.div
            className={`
              fixed
              inset-x-0
              bottom-0
              z-[130]
              mx-auto
              flex
              w-full
              max-w-[680px]
              flex-col
              overflow-hidden
              rounded-t-[28px]
              //bg-black
              select-none
              will-change-transform
              

              ${isLandscape
                ? "h-[100dvh]"
                : ""}

              ${isPortrait
                ? "h-[100dvh]"
                : ""}

              ${isStandalone
                ? "rounded-t-[30px]"
                : ""}
            `}

            style={{
    y,
    overscrollBehavior: "none",
    contain: "layout paint size",
    WebkitOverflowScrolling: "auto",
    touchAction: "pan-y",
  }}
            /* style={{
              y,
              WebkitTapHighlightColor:
                "transparent",
              touchAction:
                "pan-y",
            }} */
            drag="y"
            dragDirectionLock
            dragConstraints={{
              top: 0,
              bottom: 0,
            }}
            dragElastic={{
              top: 0,
              bottom: 0.12,
            }}
            dragMomentum={false}
            onPointerDown={
              handlePointerDown
            }
            onDragStart={
              handleDragStart
            }
            onDrag={
              handleDrag
            }
            onDragEnd={
              handleDragEnd
            }
          >

            {/* BACKGROUND */}

            <motion.div
              className="
                pointer-events-none
                absolute
                inset-0
                z-0
                overflow-hidden
              "
              animate={{
                opacity:
                  open ? 0.45 : 0,
              }}
              transition={{
                duration: 0.3,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              }}
            >
              <DrawerBackground
                image={image}
                keyId={image}
              />
            </motion.div>

            {/* HANDLE */}

            {/* <div
              className="
                relative
                z-30
                flex
                h-10
                shrink-0
                items-center
                justify-center
                touch-none
                select-none
                cursor-grab
                active:cursor-grabbing
              "
            >
              <div
                className="
                  h-1
                  w-10
                  rounded-full
                  bg-white/20
                "
              />
            </div> */}

            {/* CONTENT */}

            <motion.div
              ref={contentRef}
              className="
                relative
                z-10
                h-[100%]
                min-h-0
                flex-1
                overflow-x-hidden
                overflow-y-auto
                pb-0
                text-white
                scrollbar-none
              "
              style={{
                WebkitOverflowScrolling:
                  "touch",

                overscrollBehaviorY:
                  "contain",

                touchAction:
                  "pan-y",
              }}
              initial={{
                opacity: 0,
                y: 10,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.28,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              }}
            >
              {children}
            </motion.div>

          </motion.div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

export default memo(
  DrawerContainer
)